/**
 * PII-minimized projection contract for the durable execution/delivery ledger.
 *
 * This module intentionally contains no transition commands.  The CRM renders
 * authority and freshness; runtime state changes stay behind authenticated
 * server APIs and application RDS.
 */

export type GenerationLedgerState =
  | "created"
  | "interviewing"
  | "ready_to_compose"
  | "composing"
  | "generated"
  | "cancelled"
  | "failed_final";

export type DeliveryLedgerState =
  | "pending"
  | "claimed"
  | "submitting"
  | "accepted"
  | "delivered"
  | "read"
  | "failed_pre_submission"
  | "outcome_unknown"
  | "provider_failed"
  | "cancelled_or_erased";

export interface DurableEchoLedgerProjection {
  environment: "stg" | "staging";
  workloadId: string;
  generationState: GenerationLedgerState;
  deliveryState: DeliveryLedgerState | null;
  aggregateVersion: number;
  workflowVersion: string | null;
  workflowExecutionId: string | null;
  attemptCount: number;
  ownerHeartbeatAt: string | null;
  deliveryHeartbeatAt: string | null;
  leaseExpiresAt: string | null;
  frozenConfigReleaseId: string | null;
  frozenConfigDigest: string | null;
  artifactReceiptDigest: string | null;
  providerMessageId: string | null;
  providerStatus: "accepted" | "delivered" | "read" | "failed" | null;
  notificationState: "pending" | "accepted" | "failed" | "not_required" | null;
  refundState: "pending" | "committed" | "not_required" | null;
  reconciliationFinding: string | null;
  projectionUpdatedAt: string;
  authorityUpdatedAt: string;
}

export type DurableRecoveryActionKind =
  | "redrive_standard_execution"
  | "redrive_delivery"
  | "recover_child_execution"
  | "mark_outcome_unknown"
  | "retry_failed_pre_submission"
  | "requeue_dead_letter"
  | "request_orphan_termination";

export interface DurableRecoveryAction {
  actionId: string;
  workloadId: string;
  attemptId: string | null;
  actionKind: DurableRecoveryActionKind;
  finding: string;
  actorId: string;
  actorRole: string;
  reasonCode: string;
  ticketReference: string;
  expectedCostCents: number;
  expectedAggregateVersion: number;
  dryRun: boolean;
  createdAt: string | null;
  latestEvent: {
    sequence: number;
    type: string;
    outcome: string;
    result: Record<string, unknown> | null;
    createdAt: string | null;
  } | null;
}

export interface DurableRecoveryActionRequest {
  action_id: string;
  action_kind: DurableRecoveryActionKind;
  finding: string;
  reason_code: string;
  justification: string;
  ticket_reference: string;
  expected_cost_cents: number;
  expected_aggregate_version: number;
  dry_run: true;
}

export type ReliabilityFinding =
  | "admitted_without_workflow"
  | "workflow_without_heartbeat"
  | "generated_without_delivery_intent"
  | "delivery_without_provider_acceptance"
  | "accepted_without_webhook"
  | "outcome_unknown"
  | "terminal_before_retry_exhaustion"
  | "projection_stale";

const TERMINAL_GENERATION = new Set<GenerationLedgerState>([
  "generated",
  "cancelled",
  "failed_final",
]);

export function reliabilityFindings(
  projection: DurableEchoLedgerProjection,
  now: Date,
  staleAfterMs = 5 * 60 * 1000,
): ReliabilityFinding[] {
  const findings: ReliabilityFinding[] = [];
  if (
    projection.workflowExecutionId === null &&
    projection.generationState !== "created"
  ) {
    findings.push("admitted_without_workflow");
  }
  if (
    projection.workflowExecutionId !== null &&
    projection.generationState === "composing" &&
    projection.ownerHeartbeatAt === null
  ) {
    findings.push("workflow_without_heartbeat");
  }
  if (
    projection.generationState === "generated" &&
    projection.deliveryState === null
  ) {
    findings.push("generated_without_delivery_intent");
  }
  if (
    projection.deliveryState !== null &&
    ["pending", "claimed", "submitting"].includes(projection.deliveryState) &&
    projection.providerMessageId === null
  ) {
    findings.push("delivery_without_provider_acceptance");
  }
  if (projection.deliveryState === "outcome_unknown")
    findings.push("outcome_unknown");
  if (
    projection.deliveryState === "accepted" &&
    projection.providerMessageId !== null &&
    projection.providerStatus === "accepted" &&
    now.getTime() - new Date(projection.projectionUpdatedAt).getTime() >
      staleAfterMs
  ) {
    findings.push("accepted_without_webhook");
  }
  if (
    TERMINAL_GENERATION.has(projection.generationState) &&
    projection.attemptCount > 0 &&
    projection.generationState === "failed_final" &&
    projection.reconciliationFinding === "retryable_attempt_active"
  ) {
    findings.push("terminal_before_retry_exhaustion");
  }
  if (
    now.getTime() - new Date(projection.projectionUpdatedAt).getTime() >
    staleAfterMs
  ) {
    findings.push("projection_stale");
  }
  return findings;
}

export function displayEnvironment(
  environment: DurableEchoLedgerProjection["environment"],
): string {
  return environment === "stg"
    ? "Customer-facing production (stg)"
    : "Engineering staging (staging)";
}
